import SortableList from 'react-sortable-dnd-list'
import { StepCard } from '../StepCard'

import { useToggleStepCardsList } from './hooks/useToggleStepCardsList'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import type { Step } from '@/services/queries/operation_steps/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { useCreateOperationStep } from '@/services/queries/operation_steps'
import { useCADPageStore } from '@/state/cad'
import { QUERY_KEYS } from '@/services/queries/document_pages'
import queryClient from '@/queryClient'
import { useAppStore } from '@/state'
import { AlertBanner } from '@/components/core/AlertBanner'
import { AddPartIcon } from '@/components/icons/AddPartIcon'

export const StepCardList = ({
  documentVersionId,
  documentId,
  steps,
  isReadOnly,
}: {
  documentVersionId: string
  documentId: string
  steps: Step[]
  isReadOnly?: boolean
}) => {
  const toggleStepCardsList = useToggleStepCardsList()
  const hasDismissedNoOperationsBanner = useAppStore(
    (state) => state.hasDismissedNoOperationsBanner,
  )

  const stepsRef = useRef(steps)
  useEffect(() => {
    stepsRef.current = steps
  }, [steps])

  const sortableStepsRef = useRef(steps)
  const hasSameStepOrder =
    sortableStepsRef.current.length === steps.length &&
    sortableStepsRef.current.every(
      (step, index) => step.id === steps[index]?.id,
    )

  if (hasSameStepOrder) {
    // Keep the same array identity while refreshing item payloads.
    // This prevents sortable internals from resetting during unrelated rerenders.
    for (let index = 0; index < steps.length; index++) {
      sortableStepsRef.current[index] = steps[index]
    }
  } else {
    sortableStepsRef.current = steps
  }

  const sortableSteps = sortableStepsRef.current

  if (steps.length === 0 && !hasDismissedNoOperationsBanner) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <AlertBanner
          title="Add parts to Operations"
          description={
            <p>
              Drag parts from the Assembly Tree or click
              <span className="inline-block mx-1.5">
                <AddPartIcon className="w-4 h-4 stroke-gray-400" />
              </span>
              to select parts from CAD.
            </p>
          }
          withIcon={true}
          variant="info"
          size="sm"
        />
      </div>
    )
  }

  if (steps.length <= 1) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        {steps.map((step) => (
          <StepCardStaticItem
            key={step.id}
            step={step}
            isReadOnly={isReadOnly}
            documentId={documentId}
            documentVersionId={documentVersionId}
            steps={steps}
          />
        ))}
      </div>
    )
  }

  return (
    <SortableList
      className="flex flex-col h-full overflow-y-auto"
      animationDuration={0}
      itemComponent={StepCardItemContainer}
      itemComponentProps={{
        isReadOnly,
        documentId,
        documentVersionId,
        steps: sortableSteps,
      }}
      value={sortableSteps}
      onChange={(rawReorderedSteps: Step[]) => {
        void toggleStepCardsList({
          steps: rawReorderedSteps,
          previousSteps: stepsRef.current,
          documentVersionId,
          isReadOnly: Boolean(isReadOnly),
        })
      }}
    />
  )
}

export const StepCardItemContainer = ({
  dragging,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dragged,
  children: step,
  isReadOnly,
  documentVersionId,
  steps,
  ...rest
}: {
  dragging: boolean
  dragged: boolean
  documentVersionId: string
  isReadOnly?: boolean
  children: Step
  steps: Step[]
}) => {
  const { toast } = useToast()
  const [isHovering, setIsHovering] = useState(false)
  const { mutate: createOperationStep } = useCreateOperationStep({
    documentVersionId,
    invalidateOnSuccess: false,
  })
  const toggleStepCardsList = useToggleStepCardsList()
  const hasDismissedNoOperationsBanner = useAppStore(
    (state) => state.hasDismissedNoOperationsBanner,
  )
  const dismissNoOpBanner = useAppStore(
    (state) => state.setHasDismissedNoOperationsBanner,
  )

  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId || null,
  )
  const onCreateOperation = useCallback(async () => {
    const { documentPage: operation } = await createOperationStep({
      documentVersionId,
      parentDocumentId: subAssemblyDocumentPageId,
      orderNumber: step?.order_number || 1,
    })

    const currentStepIndex = steps.findIndex((s) => s.id === (step as Step).id)

    const updatedSteps = [
      ...steps.slice(0, currentStepIndex),
      operation,
      ...steps.slice(currentStepIndex),
    ]

    await toggleStepCardsList({
      steps: updatedSteps as Step[],
      previousSteps: steps,
      documentVersionId,
      isReadOnly: Boolean(isReadOnly),
    })

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
    })

    if (!hasDismissedNoOperationsBanner) {
      dismissNoOpBanner()
    }

    if (operation) {
      toast({
        title: `New operation added`,
        variant: 'success',
      })
    }
  }, [
    toast,
    createOperationStep,
    documentVersionId,
    subAssemblyDocumentPageId,
    steps,
    toggleStepCardsList,
    isReadOnly,
    step,
  ])

  return (
    <div
      {...rest}
      className={`${dragging ? 'is-dragging' : ''}`}
      // Ensure stable reference to prevent jumping
      {...(step?.id && { id: `sortable-list-item-${step.id}` })}
    >
      {step?.created ? (
        <>
          <div
            className="min-h-5 w-full flex items-center justify-center"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <AddOperationButton
              isHovering={isHovering}
              onClick={onCreateOperation}
            />
          </div>
          <StepCard
            stepNumber={step.order_number as number}
            step={step}
            isReadOnly={isReadOnly}
          />
        </>
      ) : (
        <div className="border-gray-300 border rounded-lg p-3 bg-white text-sm cursor-pointer">
          <div className="flex">
            <Skeleton className="h-6 w-20" />
          </div>
          <Skeleton className="p-4 h-64 w-full my-2" />
        </div>
      )}
    </div>
  )
}

const StepCardStaticItem = ({
  step,
  isReadOnly,
  documentVersionId,
  steps,
}: {
  step: Step
  documentVersionId: string
  isReadOnly?: boolean
  documentId: string
  steps: Step[]
}) => {
  const { toast } = useToast()
  const [isHovering, setIsHovering] = useState(false)
  const { mutate: createOperationStep } = useCreateOperationStep({
    documentVersionId,
    invalidateOnSuccess: false,
  })
  const toggleStepCardsList = useToggleStepCardsList()
  const toggleSingleStep = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      toggleStepCardsList({
        steps: steps,
        previousSteps: steps,
        documentVersionId,
        isReadOnly: Boolean(isReadOnly),
      }).catch(console.error)
    },
    [steps, documentVersionId, isReadOnly, toggleStepCardsList],
  )

  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId || null,
  )

  const onCreateOperation = useCallback(async () => {
    const { documentPage: operation } = await createOperationStep({
      documentVersionId,
      parentDocumentId: subAssemblyDocumentPageId,
      orderNumber: step?.order_number || 1,
    })

    const currentStepIndex = steps.findIndex((s) => s.id === step.id)

    const updatedSteps = [
      ...steps.slice(0, currentStepIndex),
      operation,
      ...steps.slice(currentStepIndex),
    ]

    await toggleStepCardsList({
      steps: updatedSteps as Step[],
      previousSteps: steps,
      documentVersionId,
      isReadOnly: Boolean(isReadOnly),
    })

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
    })

    if (operation) {
      toast({
        title: `New operation added`,
        variant: 'success',
      })
    }
  }, [
    toast,
    createOperationStep,
    documentVersionId,
    subAssemblyDocumentPageId,
    steps,
    toggleStepCardsList,
    isReadOnly,
    step,
  ])

  return (
    <div>
      {step.created ? (
        <>
          <div
            className="min-h-5 w-full flex items-center justify-center"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <AddOperationButton
              isHovering={isHovering}
              onClick={onCreateOperation}
            />
          </div>
          <div onMouseDown={toggleSingleStep}>
            <StepCard
              stepNumber={step.order_number as number}
              step={step}
              isReadOnly={isReadOnly}
            />
          </div>
        </>
      ) : (
        <div className="border-gray-300 border rounded-lg p-3 bg-white text-sm cursor-pointer">
          <div className="flex">
            <Skeleton className="h-6 w-20" />
          </div>
          <Skeleton className="p-4 h-64 w-full my-2" />
        </div>
      )}
    </div>
  )
}

type AddOperationButtonProps = {
  isHovering: boolean
  onClick: () => void
}
const AddOperationButton = ({
  isHovering,
  onClick,
}: AddOperationButtonProps) => {
  return isHovering ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="py-0.5 flex">
            <Button
              variant="ghost"
              onClick={onClick}
              className="m-0 p-0 h-auto hover:bg-slate-200"
            >
              <PlusCircleIcon className="w-4 h-4 text-slate-500" />
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-black text-white">
          <p>Add Operation</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null
}
